/* ===========================================================================
   MAVELY DEALSFLOW  v2   (MV116)  —  v1 plus two sync fixes
   ===========================================================================
   v2 = v1 with the changes below and NOTHING else. v1 stays on disk
   (mavely-dealsflow.v1.min.js) — put it back in index.html to roll back.

   FIX A — "NEW SHORTEN LINK" LOST WHEN MY SELECTED DEALS IS TOGGLED
   ---------------------------------------------------------------------------
   Reproduced exactly as delete.mp4 shows it: shorten a link, the card shows
   NEW SHORTEN LINK, tick MY SELECTED DEALS, untick it — the card is back to
   the plain product url until F5.

   Why: every list is kept in memory. The regular list in memory is the copy
   fetched BEFORE the shorten, so it cannot know the new link; the badge was
   painted on top of it from `pending`. Ticking MY SELECTED DEALS fetched that
   list fresh from the server, and confirmPending() saw the server's badge
   there and DELETED the pending entry — for every list. Unticking then served
   the old in-memory regular list with nothing left to paint the badge.

   Now a server confirmation ADOPTS the server's value instead of deleting
   it, and a list fetched before the shorten was acknowledged is always
   painted with it (it cannot contain it by construction). Every other list
   is also marked for a quiet background re-read, so it catches up with the
   server by itself. Result: the badge shows everywhere, immediately, with no
   refresh.

   FIX B — A DEAL DELETED IN MY SELECTED DEALS STAYED IN THE DEALS LIST
   ---------------------------------------------------------------------------
   v1 turned the red delete button inside MY SELECTED DEALS into
   { is_unselected: true }, which removes the deal from that list only. You
   asked for the opposite: deleting it there deletes it from the Deals List
   too, and the two stay in step. So the button now sends the same
   { is_deleted: true } in both lists — the request the app itself builds —
   and the deal is subtracted from every list in memory at once, so neither
   list shows it again, with no refresh. The server already treats
   is_deleted as "gone from both lists" (api/_deals_lib.php).
   Shortening that product again brings it back, as the server does.

   PLUS — the local badge uses the Subdomain picked in the shorten box
   (window.__mvShortOrigin from mavely-subdomain.v1), so the card never shows
   the main domain for a moment before the server's copy arrives.
   ===========================================================================
   (v1's header follows, unchanged)
   ===========================================================================
   MAVELY DEALSFLOW  v1   (MV115)  —  Tasks 3, 4 and 5
   ===========================================================================
   ONE layer between the Deals List and /api/deals, in place of the four that
   were stacked there and fought each other:

       mavely-deals-refresh.v1   cache, stale answers, its own refresh events
       mavely-deals-instant.v1   badge after link-shortened, XHR re-read, watchdog
       mavely-realtime.v1        deleted ids, device id, sort bar without reload
       mavely-dealsync.v1        outermost corrections, sort in the URL, memo

   plus the 90-second fetch cache inside mavely-dealsperf.v1 (dealsperf v2 keeps
   all of its DOM work and drops only that cache). Every behaviour those files
   provided is kept here — listed at the bottom of this comment — and their
   files are still on disk, unloaded, for rollback.

   ---------------------------------------------------------------------------
   WHY THE NEW SHORT LINK WAS SLOW (Task 4)
   ---------------------------------------------------------------------------
   One shorten fired the list refresh five or six times (the bundle's own two
   events and two refreshKey bumps, plus a nudge from each wrapper). Each one
   went through three caches keyed differently, and dealsperf answered with
   `new Response(text)`: re-serialising and re-parsing the whole 8,000-deal
   list, and — because a Response body is read in a TASK, not a microtask —
   letting the browser paint "Loading deals…" in between. So the list blanked
   and redrew several times, and the badge could be overwritten by a copy
   fetched before the server knew about the shorten.

   NOW:
     1. The moment POST /api/shorten succeeds — before the app even announces
        it — the deal is matched in the list already in memory (same product
        id or same URL) and the NEW SHORTEN LINK is stamped on a copy of that
        ONE deal object. Every other deal object is left identical.
     2. The app's own refreshes are then answered from memory, in a microtask,
        so React renders once: the loading flag is set and cleared inside one
        batch and never painted, and because only one deal object changed,
        only that card's DOM is touched. Later refreshes get the SAME object
        back and React skips them entirely. One update, only that card.
     3. When /api/deals/link-shortened answers, any extra deals the server
        matched are stamped the same way (one more update only if there is
        something new), and MY SELECTED DEALS is re-read quietly in the
        background so the new deal appears there in one update, no spinner.

   ---------------------------------------------------------------------------
   MY SELECTED DEALS (Task 3)
   ---------------------------------------------------------------------------
   The red delete button sends the same request in both lists. When it is
   pressed inside MY SELECTED DEALS, the request is sent as
   { is_unselected: true } instead of { is_deleted: true }: the deal leaves
   My Selected Deals only, and stays in the Deals List (and Hot Deals) with its
   short link. Pressed in the Deals List, it hides the deal exactly as before.
   Shortening it again brings it back (api/_deals_lib.php clears the flag).

   ---------------------------------------------------------------------------
   PERFORMANCE (Task 5)
   ---------------------------------------------------------------------------
   - ONE parsed copy per list (max 4 lists), no JSON.stringify comparisons,
     no Response re-serialisation.
   - mavely-deals v31 builds its image -> deal map from what is already in
     memory instead of downloading every deal of every store a second time.
   - When the Deals List starts below the visible screen (phones), its data
     is requested when it comes within 300px of the screen instead of during
     page load. On desktop it is on screen, so nothing changes there.

   ---------------------------------------------------------------------------
   KEPT FROM THE RETIRED FILES
   ---------------------------------------------------------------------------
   deleted ids subtracted from every list (sessionStorage mv_deleted_deals,
   same key) · device id cookie + deviceId in override bodies (v114.18) ·
   sort bar without a page reload, one price + one sold + Hot, Clear ·
   active sort in the URL so nothing can answer with the previous order ·
   window.MavelyDealsRefresh.clear() (mavely-realimages calls it) ·
   refresh after a manual deal is added · re-read after the automatic
   RESOLVING… pass · console helpers __mvRealtime / __mvDealSync /
   __mvDealsInstant (all now report this file) and __mvDealsFlow().
   ========================================================================= */
(function () {
  'use strict';

  if (window.__mvDealsFlowLoaded) return;
  window.__mvDealsFlowLoaded = true;

  var origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!origFetch) return;

  var SORT_COOKIE   = 'mv_deal_sort';
  var DEL_KEY       = 'mv_deleted_deals';      // same key realtime/dealsync used
  var UNSEL_KEY     = 'mv_unselected_deals';
  var DEVICE_KEY    = 'mv_device_id';
  var MAX_ENTRIES   = 4;                       // lists kept in memory
  var REVALIDATE_MS = 45000;                   // older than this: answer, then re-read quietly
  var PENDING_TTL   = 15 * 60 * 1000;          // a local badge is dropped after this
  var NEAR_MARGIN   = '0px 0px 300px 0px';     // "near the screen" for the first list load
  /* Set to false to load the Deals List during page load on phones too
     (the pre-MV115 behaviour). Desktop is unaffected either way. */
  var LAZY_FIRST_LIST = true;
  /* v2 (Fix B): true = the red delete button removes the deal from BOTH
     lists wherever it is pressed (what you asked for). false = v1's
     behaviour, where pressing it inside MY SELECTED DEALS only unselected it. */
  var SELECTED_DELETE_EVERYWHERE = true;

  var stats = { network: 0, instant: 0, revalidated: 0, updates: 0, stamped: 0,
                localMatches: 0, serverMatches: 0, deletes: 0, unselects: 0,
                held: 0, helperAnswers: 0, sortClicks: 0 };

  var hasOwn = Object.prototype.hasOwnProperty;
  function isArr(a) { return Object.prototype.toString.call(a) === '[object Array]'; }

  /* =======================================================================
     PER-TAB SETS
     ======================================================================= */
  function loadSet(key) {
    var s = Object.create(null);
    try {
      var a = JSON.parse(window.sessionStorage.getItem(key) || '[]');
      if (isArr(a)) for (var i = 0; i < a.length; i++) s[String(a[i])] = true;
    } catch (e) { /* private mode or corrupt: start empty */ }
    return s;
  }
  function saveSet(key, s) {
    try {
      var a = [];
      for (var k in s) if (s[k]) a.push(k);
      window.sessionStorage.setItem(key, JSON.stringify(a));
    } catch (e) {}
  }

  var deleted    = loadSet(DEL_KEY);
  var unselected = loadSet(UNSEL_KEY);
  var pending    = Object.create(null);   // id -> { link, at, ackAt }
  var corrVer    = 1;                     // bumps whenever a correction changes

  /* =======================================================================
     DEVICE ID  (from mavely-realtime v114.18, unchanged in behaviour)
     ======================================================================= */
  function deviceId() {
    var v = '';
    try { v = window.localStorage.getItem(DEVICE_KEY) || ''; } catch (e) { v = ''; }
    if (!v) {
      try {
        v = 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        window.localStorage.setItem(DEVICE_KEY, v);
      } catch (e) { return ''; }
    }
    return v;
  }
  (function publishDeviceCookie() {
    var id = deviceId();
    if (!id) return;
    try {
      if (document.cookie.indexOf(DEVICE_KEY + '=' + id) === -1) {
        document.cookie = DEVICE_KEY + '=' + encodeURIComponent(id) + ';path=/;max-age=31536000;SameSite=Lax';
      }
    } catch (e) {}
  })();

  /* =======================================================================
     URL HELPERS
     ======================================================================= */
  function pathOf(url) {
    if (typeof url !== 'string' || url === '') return '';
    if (url.indexOf('://') === -1) {
      var q = url.search(/[?#]/);
      return q === -1 ? url : url.slice(0, q);
    }
    try {
      var u = new URL(url, window.location.origin);
      return u.origin === window.location.origin ? u.pathname : '';
    } catch (e) { return ''; }
  }
  function isDealsGetUrl(url) {
    var p = pathOf(url).replace(/\/+$/, '');
    return p === '/api/deals' || p === '/api/deals.php' || p === '/api/real-deals' || p === '/api/real-deals.php';
  }
  function isSeeJust(url) { return /[?&]seeJustYourDeals=true(?:&|$)/.test(url); }
  function isManualList(url) { return /[?&]source=manual(?:&|$)/.test(url); }
  function isShorten(url) { var p = pathOf(url).replace(/\/+$/, ''); return p === '/api/shorten' || p === '/api/shorten.php'; }
  function isLinkShortened(url) {
    var p = pathOf(url).replace(/\/+$/, '');
    return p === '/api/deals/link-shortened' || p === '/api/deals-link-shortened.php' ||
           p === '/api/real-deals/link-shortened' || p === '/api/real-deals-link-shortened.php';
  }
  function overrideIdOf(url) {
    var p = pathOf(url);
    var m = /^\/api\/deals\/([A-Za-z0-9_.-]+)\/override\/?$/.exec(p);
    if (m) return m[1];
    if (p === '/api/deals-override.php') {
      try { return new URL(url, window.location.origin).searchParams.get('id') || ''; } catch (e) { return ''; }
    }
    return '';
  }
  function changesDeals(url) {
    return /manual-fetch|\/api\/admin\/deals|\/api\/admin\/real-images|import-jobs|upload-zip|upload-atom|clean-store|dedupe-images/.test(url);
  }
  function readCookie(name) {
    try {
      var m = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)').exec(document.cookie || '');
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }
  function addParam(url, k, v) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + k + '=' + encodeURIComponent(v);
  }
  function catsParam() {
    try { return (window.MavelyCats && window.MavelyCats.param) ? String(window.MavelyCats.param() || '') : ''; }
    catch (e) { return ''; }
  }
  /** The active sort and categories go INTO the URL, so every list is keyed on
      exactly what it contains. The server reads ?sort= before the cookie, and
      ?cats= only exists here — Manual Deals never gets one. */
  function withFilters(url) {
    var out = url;
    var sort = readCookie(SORT_COOKIE);
    if (sort && !/[?&]sort=/.test(out)) out = addParam(out, 'sort', sort);
    if (!isManualList(out)) {
      var c = catsParam();
      if (c && !/[?&]cats=/.test(out)) out = addParam(out, 'cats', c);
    }
    return out;
  }
  function realMode() {
    try {
      var cb = document.querySelector('#mv-real-images input[type="checkbox"]');
      return !!(cb && cb.checked);
    } catch (e) { return false; }
  }
  function keyOf(url) { return (realMode() ? 'ri|' : 'std|') + url; }
  function readBody(init) {
    try {
      var b = init && init.body;
      if (typeof b !== 'string' || !b) return null;
      var o = JSON.parse(b);
      return (o && typeof o === 'object') ? o : null;
    } catch (e) { return null; }
  }
  function withJsonBody(init, obj) {
    var out = {};
    for (var k in init) if (hasOwn.call(init, k)) out[k] = init[k];
    out.body = JSON.stringify(obj);
    return out;
  }

  /* =======================================================================
     PRODUCT MATCHING — mirrors hot_match_key() and deals_clean_url() in PHP
     ======================================================================= */
  function productKey(url) {
    url = String(url || '').trim();
    if (!/^https?:\/\//i.test(url)) return '';
    var u;
    try { u = new URL(url); } catch (e) { return ''; }
    var host = u.hostname.toLowerCase().replace(/^www\./, '');
    var path = u.pathname || '';
    var m;
    if (host.indexOf('walmart.') !== -1) {
      var all = (path + '/').match(/\/(\d{5,})(?=[\/?#]|$)/g);
      if (all && all.length) return 'walmart:' + all[all.length - 1].replace(/\D/g, '');
    }
    if (host.indexOf('amazon.') !== -1 || host === 'amzn.to') {
      m = /\/(?:dp|gp\/product|gp\/aw\/d|product)\/([A-Z0-9]{10})/i.exec(path);
      if (m) return 'amazon:' + m[1].toUpperCase();
    }
    if (host.indexOf('ebay.') !== -1 && (m = /\/itm\/(?:[^\/]*\/)?(\d{6,})/.exec(path))) return 'ebay:' + m[1];
    if (host.indexOf('etsy.') !== -1 && (m = /\/listing\/(\d+)/.exec(path))) return 'etsy:' + m[1];
    if (host.indexOf('aliexpress.') !== -1 && (m = /\/item\/(\d+)/.exec(path))) return 'ali:' + m[1];
    return '';
  }
  function cleanUrl(u) {
    var s = String(u || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
    var cut = s.search(/[?#]/);
    if (cut !== -1) s = s.slice(0, cut);
    return s.replace(/\/+$/, '');
  }
  /** Deal ids in memory that are the same product as longUrl. Exact product
      id or exact URL only — a local match can be early, never wrong. */
  function localMatch(longUrl) {
    var wantKey = productKey(longUrl), wantClean = cleanUrl(longUrl);
    if (!wantKey && wantClean.length < 10) return [];
    var out = [], seen = Object.create(null);
    for (var k in memo) {
      var raw = memo[k] && memo[k].raw;
      if (!raw || !isArr(raw.deals)) continue;
      for (var i = 0; i < raw.deals.length; i++) {
        var d = raw.deals[i];
        if (!d || d.id === undefined || seen[d.id]) continue;
        var master = d.product_link || (d.is_shortened ? '' : d.comment_link) || '';
        if (!master) continue;
        var hit = (wantKey && productKey(master) === wantKey) ||
                  (wantClean.length >= 10 && cleanUrl(master) === wantClean);
        if (hit) { seen[d.id] = true; out.push(String(d.id)); }
      }
    }
    return out;
  }

  /* =======================================================================
     THE MEMORY — one parsed copy per list
     ======================================================================= */
  var memo = Object.create(null);   // key -> { url, raw, out, ver, at, seeJust }
  var lru = [];
  var inflight = Object.create(null);
  var latestRaw = null;             // for hotStyle/hotImage/hotEffects
  var helperWaiters = [];

  function touch(key) {
    var i = lru.indexOf(key);
    if (i !== -1) lru.splice(i, 1);
    lru.push(key);
    while (lru.length > MAX_ENTRIES) { var old = lru.shift(); delete memo[old]; }
  }
  function put(key, url, raw, started) {
    memo[key] = { url: url, raw: raw, out: null, ver: 0, at: Date.now(), seeJust: isSeeJust(url),
                  /* v2 (Fix A): when this copy was REQUESTED. A copy requested
                     before the server acknowledged a new short link cannot
                     contain it, so correct() always paints the badge on it. */
                  started: started || Date.now() };
    touch(key);
    latestRaw = raw;
    return memo[key];
  }
  function dropAll() {
    memo = Object.create(null);
    lru = [];
    corrVer++;
  }

  /* A stamped deal is a COPY; the copy is reused while its source object and
     link are unchanged, so repeated answers hand React identical references. */
  var stampCache = Object.create(null);
  function stamped(d, link) {
    var c = stampCache[d.id];
    if (c && c.src === d && c.link === link) return c.copy;
    var copy = {};
    for (var k in d) if (hasOwn.call(d, k)) copy[k] = d[k];
    copy.is_shortened = true;
    if (link) copy.custom_short_link = link;
    stampCache[d.id] = { src: d, link: link, copy: copy };
    stats.stamped++;
    return copy;
  }

  /** Deleted ids out of every list, unselected ids out of My Selected Deals,
      local badges in. Returns `raw` itself when there is nothing to change. */
  function correct(raw, seeJust, started) {
    if (!raw || !isArr(raw.deals)) return raw;
    var src = raw.deals, out = null, removed = 0;
    var now = Date.now();
    for (var i = 0; i < src.length; i++) {
      var d = src[i], nd = d;
      if (d && typeof d === 'object') {
        var id = String(d.id);
        if (deleted[id] || (seeJust && unselected[id])) {
          if (!out) out = src.slice(0, i);
          removed++;
          continue;
        }
        var p = pending[id];
        if (p && !(d.is_shortened && d.custom_short_link && d.custom_short_link === p.link)) {
          /* v2 (Fix A): v1 painted only within PENDING_TTL. A copy that was
             requested before the server acknowledged the link cannot hold it,
             so it is painted however old it is — that is the copy the
             MY SELECTED DEALS toggle used to hand back without the badge. */
          var recent = now - p.at < PENDING_TTL;
          var older  = !!(p.ackAt && started && started < p.ackAt);
          if (recent || older) nd = stamped(d, p.link);
        }
      }
      if (out) out.push(nd);
      else if (nd !== d) { out = src.slice(0, i); out.push(nd); }
    }
    if (!out) return raw;
    var res = {};
    for (var k in raw) if (hasOwn.call(raw, k)) res[k] = raw[k];
    res.deals = out;
    if (removed) {
      if (typeof res.total === 'number') res.total = Math.max(0, res.total - removed);
      if (typeof res.count === 'number') res.count = Math.max(0, res.count - removed);
    }
    return res;
  }

  function sameList(a, b) {
    if (a === b) return true;
    if (!a || !b || !isArr(a.deals) || !isArr(b.deals) || a.deals.length !== b.deals.length) return false;
    for (var i = 0; i < a.deals.length; i++) if (a.deals[i] !== b.deals[i]) return false;
    return true;
  }

  /** The corrected list for an entry, as the SAME object while nothing in it
      changed — which is what lets React skip every repeat refresh. */
  function view(e) {
    if (e.out && e.ver === corrVer) return e.out;
    var fresh = correct(e.raw, e.seeJust, e.started);
    if (e.out && sameList(e.out, fresh)) { e.ver = corrVer; return e.out; }
    e.out = fresh;
    e.ver = corrVer;
    return fresh;
  }

  /** Did anything the cards show change? Field by field; no serialising. */
  function differs(a, b) {
    var da = a && a.deals, db = b && b.deals;
    if (!isArr(da) || !isArr(db) || da.length !== db.length) return true;
    for (var i = 0; i < da.length; i++) {
      var x = da[i], y = db[i];
      if (x === y) continue;
      if (!x || !y) return true;
      if (x.id !== y.id || !!x.is_shortened !== !!y.is_shortened ||
          (x.custom_short_link || '') !== (y.custom_short_link || '') ||
          (x.comment_link || '') !== (y.comment_link || '') ||
          (x.description || '') !== (y.description || '') ||
          (x.image_path || '') !== (y.image_path || '') ||
          !!x.is_hot !== !!y.is_hot || (x.hot_clicks || 0) !== (y.hot_clicks || 0) ||
          (x.price || '') !== (y.price || '') || (x.old_price || '') !== (y.old_price || '') ||
          (x.discount || '') !== (y.discount || '') || (x.copy_count || 0) !== (y.copy_count || 0) ||
          !!x.is_new !== !!y.is_new) return true;
    }
    return false;
  }

  /** A network answer that shows the server has stored a badge confirms the
      local one. After the server acknowledged the shorten, its value wins.

      v2 (Fix A): v1 DELETED the local badge here, for every list at once —
      so one fresh list (My Selected Deals) retired the badge that an older
      list in memory (the Deals List) still depended on. It is kept now and
      takes the server's value, so every list shows the same link. */
  function confirmPending(raw, started) {
    if (!raw || !isArr(raw.deals)) return;
    var any = false, k;
    for (k in pending) { any = true; break; }
    if (!any) return;
    var changed = false;
    for (var i = 0; i < raw.deals.length; i++) {
      var d = raw.deals[i];
      if (!d) continue;
      var p = pending[String(d.id)];
      if (!p) continue;
      if (d.is_shortened && d.custom_short_link &&
          (d.custom_short_link === p.link || (p.ackAt && started > p.ackAt))) {
        if (p.link !== d.custom_short_link) { p.link = d.custom_short_link; changed = true; }
        p.confirmed = true;
      }
    }
    if (changed) corrVer++;
  }

  function addPending(ids, link, ack) {
    var now = Date.now(), changed = false, unselChanged = false, delChanged = false;
    for (var i = 0; i < ids.length; i++) {
      var id = String(ids[i]);
      if (!id) continue;
      if (unselected[id]) { delete unselected[id]; unselChanged = true; }
      /* v2 (Fix B): a deleted deal that the SERVER has just matched to a new
         short link is live again there (handle_link_shortened_for_visitor
         clears is_deleted), so it comes back here too. Only on the server's
         answer, never on a local guess. */
      if (ack && deleted[id]) { delete deleted[id]; delChanged = true; }
      var p = pending[id];
      if (p && p.link === link) { p.at = now; if (ack) p.ackAt = now; if (delChanged) changed = true; continue; }
      pending[id] = { link: link, at: now, ackAt: ack ? now : 0 };
      changed = true;
    }
    if (unselChanged) saveSet(UNSEL_KEY, unselected);
    if (delChanged) saveSet(DEL_KEY, deleted);
    if (changed || unselChanged || delChanged) corrVer++;
    return changed || unselChanged || delChanged;
  }

  /* =======================================================================
     ONE REFRESH, COALESCED
     =======================================================================
     sh_link_shortened is the event the Deals List component already listens
     for. It is only sent when something the list shows actually changed. */
  var nudgeTimer = null;
  function nudge() {
    if (nudgeTimer) return;
    nudgeTimer = window.setTimeout(function () {
      nudgeTimer = null;
      stats.updates++;
      try { window.dispatchEvent(new Event('sh_link_shortened')); } catch (e) {}
    }, 80);
  }

  function stub(data, url) {
    return {
      ok: true, status: 200, statusText: 'OK',
      headers: {
        get: function (h) { return String(h).toLowerCase() === 'content-type' ? 'application/json' : null; },
        has: function (h) { return String(h).toLowerCase() === 'content-type'; }
      },
      url: url || '', redirected: false, type: 'basic', bodyUsed: false,
      json: function () { return Promise.resolve(data); },
      text: function () { return Promise.resolve(JSON.stringify(data)); },
      clone: function () { return stub(data, url); }
    };
  }

  /** One network read per list at a time; the parsed JSON is shared. */
  function network(key, url, init) {
    if (inflight[key]) return inflight[key];
    var started = Date.now();
    stats.network++;
    var p = origFetch(url, init).then(function (r) {
      if (!r || !r.ok || typeof r.clone !== 'function') return { res: r };
      return r.clone().json().then(function (data) {
        return { res: r, data: data, started: started };
      }, function () { return { res: r }; });
    });
    inflight[key] = p;
    p.then(function () { delete inflight[key]; }, function () { delete inflight[key]; });
    return p;
  }

  /** Answer now, re-read behind it; one refresh only if the list changed. */
  function revalidate(key) {
    var e = memo[key];
    if (!e || inflight[key]) return;
    stats.revalidated++;
    network(key, e.url, undefined).then(function (r) {
      if (!r || !r.data) return;
      confirmPending(r.data, r.started);
      var cur = memo[key];
      if (!cur) return;
      if (differs(cur.raw, r.data)) {
        put(key, cur.url, r.data, r.started);
        nudge();
      } else {
        cur.at = Date.now();
        /* v2 (Fix A): the copy in memory is as new as this read, so it is
           dated from it; repaint only if that changes what the cards show. */
        if (r.started && r.started > (cur.started || 0)) {
          var before = view(cur);
          cur.started = r.started;
          cur.ver = 0;
          if (view(cur) !== before) nudge();
        }
      }
    }, function () {});
  }

  /* =======================================================================
     mavely-deals keeps an image -> deal map for prices and HOT badges. It
     used to fill it by downloading every deal of every store a second time.
     It is answered from memory now: every list already loaded, newest first,
     one entry per picture. Its first request simply waits for the first list.
     ======================================================================= */
  function isHelperRequest(url, init) {
    return !!init && init.credentials === 'same-origin' && /^\/api\/deals\?source=deals$/.test(url);
  }
  function unionPayload() {
    var seen = Object.create(null), deals = [];
    for (var i = lru.length - 1; i >= 0; i--) {
      var e = memo[lru[i]];
      if (!e) continue;
      var v = view(e);
      if (!v || !isArr(v.deals)) continue;
      for (var j = 0; j < v.deals.length; j++) {
        var d = v.deals[j];
        var k = d && (d.image_path || ('#' + d.id));
        if (!d || seen[k]) continue;
        seen[k] = true;
        deals.push(d);
      }
    }
    var out = { success: true, deals: deals };
    if (latestRaw) {
      if (latestRaw.hotStyle !== undefined)   out.hotStyle = latestRaw.hotStyle;
      if (latestRaw.hotImage !== undefined)   out.hotImage = latestRaw.hotImage;
      if (latestRaw.hotEffects !== undefined) out.hotEffects = latestRaw.hotEffects;
    }
    return out;
  }
  function helperAnswer(url) {
    stats.helperAnswers++;
    if (lru.length) return Promise.resolve(stub(unionPayload(), url));
    return new Promise(function (resolve) { helperWaiters.push(function () { resolve(stub(unionPayload(), url)); }); });
  }
  function releaseHelpers() {
    if (!helperWaiters.length) return;
    var w = helperWaiters; helperWaiters = [];
    for (var i = 0; i < w.length; i++) { try { w[i](); } catch (e) {} }
  }

  /* =======================================================================
     THE FIRST LIST LOAD, ONLY WHEN IT CAN BE SEEN
     =======================================================================
     On a phone the Deals List starts below the hero and the shortener. Its
     data (several MB for 8,000+ deals) and its pictures are then fetched when
     it comes within 300px of the screen. On desktop it is on screen at once
     and nothing waits. Without IntersectionObserver nothing waits either. */
  var firstListDone = false;
  function whenNear() {
    return new Promise(function (resolve) {
      var done = false, io = null;
      function go() {
        if (done) return;
        done = true;
        try { if (io) io.disconnect(); } catch (e) {}
        resolve();
      }
      var els = [];
      try { els = document.querySelectorAll('.custom-scrollbar[class*="max-h-[440px]"]'); } catch (e) {}
      if (!els.length || !window.IntersectionObserver) return go();
      try {
        io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) { go(); return; }
          stats.held++;
        }, { rootMargin: NEAR_MARGIN });
        for (var i = 0; i < els.length; i++) io.observe(els[i]);
      } catch (e) { go(); }
    });
  }

  function dealsGet(input, init, url) {
    if (isHelperRequest(url, init)) return helperAnswer(url);

    var full = withFilters(url);
    /* Download ZIP (mavely-zipdesign) always reads the live list. */
    if (/[?&]_mvzip=/.test(url)) return origFetch(full, init);

    var key = keyOf(full);
    var e = memo[key];
    if (e) {
      touch(key);
      stats.instant++;
      if (Date.now() - e.at > REVALIDATE_MS) revalidate(key);
      return Promise.resolve(stub(view(e), full));
    }

    function go() {
      return network(key, full, init).then(function (r) {
        if (!r || !r.data) return r ? r.res : r;
        confirmPending(r.data, r.started);
        var entry = put(key, full, r.data, r.started);
        releaseHelpers();
        return stub(view(entry), full);
      });
    }
    if (LAZY_FIRST_LIST && !init && !firstListDone) {
      firstListDone = true;
      return whenNear().then(go);
    }
    firstListDone = true;
    return go();
  }

  /* =======================================================================
     SHORTEN — the badge is stamped before the app announces the new link
     ======================================================================= */
  function composed(link, useDesc, desc) {
    desc = (typeof desc === 'string') ? desc.trim() : '';
    return (useDesc && desc) ? desc + ' ' + link : link;
  }

  function onShorten(input, init) {
    var body = readBody(init);
    return origFetch(input, init).then(function (res) {
      if (!res || !res.ok || typeof res.clone !== 'function' || !body || !body.url) return res;
      return res.clone().json().then(function (j) {
        try {
          if (j && j.success && j.code) {
            /* The app builds the link as origin + '/' + code and stores
               "description + link" when the description option is on —
               composed here the same way, so the card text never changes
               a moment later when the server's copy arrives. */
            var desc = (typeof body.customDescription === 'string') ? body.customDescription : '';
            /* v2: the same origin the app now builds the link with — the
               Subdomain picked in the shorten box, when there is one. */
            var base = window.location.origin;
            try {
              if (typeof window.__mvShortOrigin === 'function') base = window.__mvShortOrigin(j.code) || base;
            } catch (e2) { base = window.location.origin; }
            var text = composed(base + '/' + j.code, !!desc, desc);
            var ids = localMatch(String(body.url));
            stats.localMatches += ids.length;
            /* No refresh is sent here: the app refreshes the list itself
               immediately after this resolves, and that refresh now carries
               the badge. */
            if (ids.length) addPending(ids, text, false);
          }
        } catch (e) {}
        return res;
      }, function () { return res; });
    });
  }

  /** Re-read My Selected Deals quietly; it gains the new deal. */
  function refreshSelectedLists() {
    for (var k in memo) if (memo[k] && memo[k].seeJust) revalidate(k);
  }

  function onLinkShortened(input, init) {
    var body = readBody(init);
    var link = body && typeof body.newShortlink === 'string' ? body.newShortlink : '';
    var text = link ? composed(link, !!(body.useDescription || body.appendDescription), body.customDescription) : '';
    return origFetch(input, init).then(function (res) {
      try {
        if (res && res.ok && typeof res.clone === 'function') {
          res.clone().json().then(function (j) {
            var ids = (j && isArr(j.matchedIds)) ? j.matchedIds : [];
            stats.serverMatches += ids.length;
            if (ids.length && text && addPending(ids, text, true)) nudge();
            /* v2 (Fix A): every list held in memory predates this link. They
               keep showing the badge from `pending`, and are re-read quietly
               the next time they are shown so they catch up with the server. */
            for (var mk in memo) if (memo[mk]) memo[mk].at = 0;
            refreshSelectedLists();
          }, function () {});
        }
      } catch (e) {}
      return res;
    });
  }

  /* =======================================================================
     DELETE — which list was the button in?
     ======================================================================= */
  var lastDelete = null;

  function englishTextOf(node) {
    try {
      var own = node.getAttribute && node.getAttribute('data-mv-en');
      if (own) return own.replace(/\s+/g, ' ').trim().toUpperCase();
      var marked = node.querySelector && node.querySelector('[data-mv-en]');
      if (marked && marked.getAttribute('data-mv-en')) {
        return marked.getAttribute('data-mv-en').replace(/\s+/g, ' ').trim().toUpperCase();
      }
    } catch (e) {}
    return String(node.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
  }

  function selectedToggleOf(root) {
    var zr = root.querySelector('.mv-ziprow');
    var scope = zr || root;
    var labels = scope.querySelectorAll('label');
    for (var i = 0; i < labels.length; i++) {
      var t = englishTextOf(labels[i]);
      if (t === 'MY SELECTED DEALS' || t === 'عروضي المحددة') {
        var cb = labels[i].querySelector('input[type="checkbox"]');
        if (cb) return cb;
      }
    }
    return null;
  }

  function inSelectedView(btn) {
    var n = btn;
    for (var i = 0; i < 14 && n; i++) {
      n = n.parentElement;
      if (!n || !n.querySelector) break;
      if (!n.querySelector('.mv-ziprow, label input[type="checkbox"]')) continue;
      var cb = selectedToggleOf(n);
      if (cb) return !!cb.checked;
    }
    return false;
  }

  window.addEventListener('click', function (e) {
    try {
      var b = e.target && e.target.closest ? e.target.closest('button') : null;
      if (!b || String(b.className || '').indexOf('bg-rose-600') === -1) return;
      var row = b.parentElement;
      if (!row || !row.querySelector('img[alt="Deal product"]')) return;
      lastDelete = { at: Date.now(), fromSelected: inSelectedView(b) };
    } catch (err) { lastDelete = null; }
  }, true);

  function onOverride(oid, input, init) {
    var body = readBody(init);
    if (!body) return origFetch(input, init);
    var out = {};
    for (var k in body) if (hasOwn.call(body, k)) out[k] = body[k];
    var did = deviceId();
    if (did && !out.deviceId) out.deviceId = did;

    var unsel = false, del = false;
    if (out.is_deleted === true) {
      if (!SELECTED_DELETE_EVERYWHERE &&
          lastDelete && lastDelete.fromSelected && (Date.now() - lastDelete.at) < 2500) {
        delete out.is_deleted;
        out.is_unselected = true;
        unsel = true;
      } else {
        /* v2 (Fix B): pressed in either list, the delete is a real delete —
           the exact request the app built — so the deal leaves BOTH lists. */
        if (lastDelete && lastDelete.fromSelected) stats.selectedDeletes = (stats.selectedDeletes || 0) + 1;
        del = true;
      }
    }
    lastDelete = null;

    var id = String(oid);
    if (unsel) { unselected[id] = true; saveSet(UNSEL_KEY, unselected); stats.unselects++; corrVer++; }
    if (del)   { deleted[id] = true;    saveSet(DEL_KEY, deleted);       stats.deletes++;   corrVer++; }

    function release() {
      if (unsel) { delete unselected[id]; saveSet(UNSEL_KEY, unselected); corrVer++; }
      if (del)   { delete deleted[id];    saveSet(DEL_KEY, deleted);       corrVer++; }
    }
    /* No refresh is asked for: the list removes the card from its own state
       on success, and every later answer already reflects the change. */
    return origFetch(input, withJsonBody(init, out)).then(function (r) {
      if (!r || !r.ok) release();
      return r;
    }, function (err) { release(); throw err; });
  }

  /* =======================================================================
     THE SORT BAR WITHOUT A PAGE RELOAD  (from mavely-realtime, plus Clear
     also clearing the category buttons, and matching translated labels)
     ======================================================================= */
  var SORT_KEYS = { 'HOT DEALS': 'hot', 'HIGH PRICE': 'price_high', 'LOW PRICE': 'price_low',
                    'HIGH SOLD %': 'sold_high', 'LOW SOLD %': 'sold_low' };
  function readSorts() {
    var out = [];
    readCookie(SORT_COOKIE).split(',').forEach(function (s) { s = s.trim(); if (s) out.push(s); });
    return out;
  }
  function groupOf(key) { return key === 'hot' ? 'hot' : (key.indexOf('price') === 0 ? 'price' : 'sold'); }
  function writeSorts(list) {
    var v = list.join(',');
    document.cookie = SORT_COOKIE + '=' + encodeURIComponent(v) + ';path=/;max-age=' + (v ? 2592000 : 0) + ';SameSite=Lax';
  }

  function softSort(btn) {
    var text = englishTextOf(btn);
    if (btn.classList.contains('mv-sortclear') || text === 'CLEAR') {
      writeSorts([]);
      try { if (window.MavelyCats && window.MavelyCats.clear) window.MavelyCats.clear(true); } catch (e) {}
    } else {
      var key = SORT_KEYS[text];
      if (!key) return false;                       // unknown button: leave it alone
      var group = groupOf(key), next = [], had = false;
      readSorts().forEach(function (k) {
        if (groupOf(k) === group) { if (k === key) had = true; }
        else next.push(k);
      });
      if (!had) next.push(key);
      next.sort(function (a) { return groupOf(a) === 'price' ? -1 : 1; });
      writeSorts(next);
    }
    var active = readSorts(), bar = btn.parentElement;
    if (bar) {
      var all = bar.querySelectorAll('.mv-sortbtn');
      for (var i = 0; i < all.length; i++) {
        if (all[i].hasAttribute('data-mv-cat')) continue;
        var k2 = SORT_KEYS[englishTextOf(all[i])];
        if (k2) all[i].classList.toggle('is-on', active.indexOf(k2) !== -1);
      }
    }
    try { if (window.MavelyCats && window.MavelyCats.sync) window.MavelyCats.sync(); } catch (e) {}
    try { if (window.MavelyDeals && window.MavelyDeals.reload) window.MavelyDeals.reload(); } catch (e) {}
    stats.sortClicks++;
    nudge();
    return true;
  }

  document.addEventListener('click', function (e) {
    var n = e.target;
    for (var i = 0; i < 4 && n; i++) {
      if (n.classList && n.classList.contains('mv-sortbtn') && !n.hasAttribute('data-mv-cat')) {
        if (softSort(n)) {
          e.preventDefault();
          e.stopImmediatePropagation();          // the old reload handler never runs
        }
        return;
      }
      n = n.parentElement;
    }
  }, true);

  /* =======================================================================
     THE FETCH WRAP — outermost, loaded last
     ======================================================================= */
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && typeof input.url === 'string' ? input.url : '');
    var method = String((init && init.method) || (input && typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    try {
      if (typeof input === 'string') {
        if (method === 'GET' && isDealsGetUrl(url)) return dealsGet(input, init, url);
        if (method === 'POST') {
          if (isShorten(url)) return onShorten(input, init);
          if (isLinkShortened(url)) return onLinkShortened(input, init);
          var oid = overrideIdOf(url);
          if (oid) return onOverride(oid, input, init);
        }
        if (method !== 'GET' && changesDeals(url)) {
          return origFetch(input, init).then(function (r) { if (r && r.ok) dropAll(); return r; });
        }
      }
    } catch (e) { /* fall through untouched */ }
    return origFetch(input, init);
  };

  ['mv_deals_changed', 'mv_deal_saved'].forEach(function (ev) {
    window.addEventListener(ev, dropAll);
  });
  /* The automatic RESOLVING… pass may have rewritten a record: re-read
     quietly, refresh only if something shown changed. */
  window.addEventListener('mavely:link-resolved', function () {
    for (var k in memo) revalidate(k);
  });

  /* =======================================================================
     PUBLIC HANDLES — the old names keep working
     ======================================================================= */
  function resetAll() {
    deleted = Object.create(null); unselected = Object.create(null); pending = Object.create(null);
    saveSet(DEL_KEY, deleted); saveSet(UNSEL_KEY, unselected);
    dropAll(); nudge();
  }
  function markDeletedApi(id) { deleted[String(id)] = true; saveSet(DEL_KEY, deleted); corrVer++; }
  function isDeletedApi(id) { return !!deleted[String(id)]; }

  window.MavelyDealsFlow = { refresh: nudge, drop: dropAll, matchLocal: localMatch };
  window.MavelyDealsRefresh = {
    clear: dropAll,
    peek: function () {
      var o = {};
      for (var k in memo) if (memo[k]) o[memo[k].url] = { at: memo[k].at, data: view(memo[k]), stale: false };
      return o;
    }
  };
  window.__mvRealtimeApi = { markDeleted: markDeletedApi, isDeleted: isDeletedApi,
                             strip: function (d) { return correct(d, false); }, staleAll: dropAll, reset: resetAll };
  window.__mvDealSyncApi = { markDeleted: markDeletedApi, invalidate: dropAll, isDeleted: isDeletedApi,
                             correct: function (d) { return correct(d, false); }, reset: resetAll };
  window.__mvDealsFlow = function () {
    var o = {};
    for (var k in stats) if (hasOwn.call(stats, k)) o[k] = stats[k];
    o.lists = lru.map(function (key) { return memo[key] ? memo[key].url + ' (' + ((memo[key].raw && memo[key].raw.deals) || []).length + ')' : key; });
    o.deleted = Object.keys(deleted);
    o.unselected = Object.keys(unselected);
    o.pendingBadges = Object.keys(pending);
    o.activeSort = readCookie(SORT_COOKIE);
    o.categories = catsParam();
    return o;
  };
  window.__mvRealtime = window.__mvDealSync = window.__mvDealsInstant = window.__mvDealsFlow;

  if (window.console && console.info) {
    console.info('[mavely-dealsflow] v2 ready \u2014 one data layer for the Deals List (badge + delete stay in sync)');
  }
})();
